<template>
  <div
    ref="dropdown"
    class="o-dropdown"
    :class="classes"
    @mouseover.stop
  >
    <div
      class="o-dropdown__toggle"
      :tabindex="TabIndex.ENABLED"
      :aria-label="toggleAriaLabel"
      @keydown.enter="toggle"
      @click="activatorClick"
    >
      <slot name="activator"></slot>
    </div>
    <div
      ref="wrapperRef"
      class="o-dropdown__wrapper"
      :style="wrapperStyle"
    >
      <div class="o-dropdown__content">
        <slot></slot>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { computed, onBeforeUnmount, onMounted, onUnmounted, ref, watch } from 'vue';
  import { useContext } from '@/shared/composables';
  import { TabIndex } from '@/shared/enums';
  import { NavigationContextManager } from '@/shared/utils/accessibility';

  const { $i18n } = useContext();

  const props = defineProps({
    maxWidth: Number,
    position: {
      type: String,
      default: 'bottom',
    },
    activateOnClick: Boolean,
    ariaLabel: String,
  });

  const isOpen = ref(false);
  const dropdown = ref(null);
  const wrapperRef = ref(null);

  const wrapperStyle = computed(() => ({
    width: props.maxWidth && `${props.maxWidth}px`,
  }));

  const classes = computed(() => [
    `o-dropdown--${props.position}`,
    `o-dropdown--activator-${props.activateOnClick ? 'click' : 'hover'}`,
    { 'is-active': isOpen.value },
  ]);

  const toggleAriaLabel = computed(() => {
    return props.ariaLabel || $i18n.t('accessibility.dropdown.toggle');
  });

  function activatorClick() {
    if (!props.activateOnClick) {
      return;
    }

    toggle();
  }

  function clickOutside(event) {
    if (isOpen.value && dropdown.value && !(dropdown.value === event.target || dropdown.value.contains(event.target))) {
      isOpen.value = false;
    }
  }

  function close() {
    isOpen.value = false;
  }

  function toggle() {
    isOpen.value = !isOpen.value;
  }

  watch(isOpen, () => {
    isOpen.value
      ? NavigationContextManager.pushContext(wrapperRef.value, close)
      : NavigationContextManager.popContext();
  });

  onMounted(() => {
    if (props.activateOnClick) {
      window.addEventListener('click', clickOutside);
    }
  });

  onUnmounted(() => {
    if (props.activateOnClick) {
      window.removeEventListener('click', clickOutside);
    }
  });

  onBeforeUnmount(() => {
    if (isOpen.value) {
      NavigationContextManager.popContext();
    }
  });

  defineExpose({
    close,
  });
</script>
